import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  UserRound,
  Wrench,
  UserCheck,
  Pencil,
  Phone,
  MessageCircle,
  Copy,
  Check,
  MapPin,
  Flag,
  Ticket,
  CalendarClock,
  Quote,
} from "lucide-react";
import { dealsAPI } from "../../services/api";
import {
  PRIORITY_LEVELS,
  PRIORITY_COLORS,
  TATWEER_SERVICE_CATEGORY_OPTIONS,
  TATWEER_REQUEST_TYPE_OPTIONS,
  TATWEER_FOLLOW_UP_REQUEST,
} from "../../utils/constants";

// Service Request tab for the Tatweer client: shown in place of Lead Profile.
// Every field from the Tatweer Create Lead form, grouped and editable in place.
// Status (stage) and technician (assignee) are changed with the controls at the
// top of the lead page, so they are read-only here.

const SECTIONS = [
  {
    title: "Customer details",
    icon: UserRound,
    color: "#6366F1",
    fields: [
      { key: "dealName", label: "Customer name", required: true },
      { key: "mobile", label: "Mobile no", readOnly: true, copy: true },
      { key: "area", label: "Area / community", required: true },
      { key: "buildingName", label: "Building name" },
      { key: "unitNo", label: "Apt / villa no" },
      { key: "priority", label: "Priority", type: "select", options: PRIORITY_LEVELS },
    ],
  },
  {
    title: "Service details",
    icon: Wrench,
    color: "#0EA5E9",
    fields: [
      { key: "serviceCategory", label: "Service category", type: "select", options: TATWEER_SERVICE_CATEGORY_OPTIONS, required: true },
      { key: "requestType", label: "Request type", type: "select", options: TATWEER_REQUEST_TYPE_OPTIONS, required: true },
      { key: "existingTicketId", label: "Existing ticket ID", copy: true },
      { key: "preferredDateTime", label: "Preferred date & time", type: "datetime" },
      { key: "description", label: "Issue description", multiline: true, full: true, required: true },
    ],
  },
  {
    title: "Assignment & billing",
    icon: UserCheck,
    color: "#10B981",
    fields: [
      { key: "__stage", label: "Status", readOnly: true, hint: "Change it with the pipeline at the top of the page" },
      { key: "__technician", label: "Technician", readOnly: true, hint: "Change it with “Assigned to” at the top of the page" },
      { key: "amount", label: "Charges (AED)", type: "number" },
      { key: "remarks", label: "Remarks", multiline: true, full: true },
    ],
  },
];

const ALL_FIELDS = SECTIONS.flatMap((s) => s.fields);
const EDITABLE_KEYS = ALL_FIELDS.filter((f) => !f.readOnly).map((f) => f.key);
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const isEmpty = (v) => v === undefined || v === null || String(v).trim() === "";
const capitalize = (s) => String(s).charAt(0).toUpperCase() + String(s).slice(1);

// "2026-10-30T14:30" (datetime-local) → "30 Oct 2026, 2:30 PM"
const formatDateTime = (v) => {
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const h = d.getHours();
  const time = `${h % 12 || 12}:${String(d.getMinutes()).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${time}`;
};

const formatAmount = (v) => `AED ${Number(v).toLocaleString("en-AE", { maximumFractionDigits: 2 })}`;

const initials = (deal) =>
  String(deal.dealName || deal.name || "?").split(/\s+/).map((n) => n[0]).slice(0, 2).join("").toUpperCase();

// One line of text that never breaks mid-word; full value on hover
const OneLine = ({ children, title }) => (
  <Box component="span" title={title} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
    {children}
  </Box>
);

const CopyButton = ({ value }) => {
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? "Copied" : "Copy"} placement="top">
      <IconButton
        size="small"
        className="copy-btn"
        onClick={() => {
          navigator.clipboard?.writeText(String(value)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        sx={{ p: 0.4, color: copied ? "#16a34a" : "#94a3b8", opacity: copied ? 1 : 0, transition: "opacity .15s" }}
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </IconButton>
    </Tooltip>
  );
};

const renderValue = (field, value) => {
  if (field.key === "priority") {
    const c = PRIORITY_COLORS[String(value).toLowerCase()];
    return (
      <Chip
        label={capitalize(value)}
        size="small"
        sx={{ bgcolor: c?.light || "#f1f5f9", color: c?.text || "#334155", fontWeight: 700, fontSize: 12, height: 22 }}
      />
    );
  }
  if (field.type === "datetime") return formatDateTime(value);
  if (field.type === "number") return formatAmount(value);
  return field.multiline ? String(value) : <OneLine title={String(value)}>{String(value)}</OneLine>;
};

const ServiceRequestTab = ({ deal, onPatched, assigneeName }) => {
  const isViewer = localStorage.getItem("userRole") === "viewer";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const valueOf = (key) => {
    if (key === "__stage") return deal.stage?.name || deal.stageName;
    if (key === "__technician") return assigneeName;
    if (key === "amount") return deal.amount || ""; // 0 = no charges entered
    return deal[key];
  };

  const isFollowUp = (editing ? form.requestType : deal.requestType) === TATWEER_FOLLOW_UP_REQUEST;
  // Ticket ID only applies to follow-ups
  const isRequired = (field) => field.required || (field.key === "existingTicketId" && isFollowUp);
  const isVisible = (field) => field.key !== "existingTicketId" || isFollowUp || !isEmpty(deal.existingTicketId);

  const startEdit = () => {
    setForm(
      EDITABLE_KEYS.reduce((acc, key) => {
        acc[key] = key === "priority" ? deal.priority || "medium" : valueOf(key) ?? "";
        return acc;
      }, {})
    );
    setError(null);
    setEditing(true);
  };

  const handleChange = (key, value) =>
    setForm((p) => ({
      ...p,
      [key]: value,
      ...(key === "requestType" && value !== TATWEER_FOLLOW_UP_REQUEST ? { existingTicketId: "" } : {}),
    }));

  const handleSave = async () => {
    const missing = ALL_FIELDS.filter((f) => !f.readOnly && isRequired(f) && isEmpty(form[f.key]));
    if (missing.length) {
      setError(`Please fill in: ${missing.map((f) => f.label.toLowerCase()).join(", ")}`);
      return;
    }
    if (!isEmpty(form.amount) && (isNaN(Number(form.amount)) || Number(form.amount) < 0)) {
      setError("Charges must be a positive number");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      // The API ignores a null amount, so clearing charges saves 0 (shown as empty)
      const patch = { ...form, amount: isEmpty(form.amount) ? 0 : Number(form.amount) };
      const res = await dealsAPI.update(deal._id, patch);
      onPatched?.(res.data?.deal || patch);
      setEditing(false);
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't save the service request.");
    } finally {
      setSaving(false);
    }
  };

  const visibleFields = ALL_FIELDS.filter(isVisible);
  const filledCount = visibleFields.filter((f) => !isEmpty(valueOf(f.key))).length;
  const location = [deal.unitNo, deal.buildingName, deal.area].filter(Boolean).join(", ");

  // Key facts shown as chips in the summary card
  const facts = [
    { icon: Wrench, label: deal.serviceCategory, color: "#0369a1", bg: "#f0f9ff" },
    { icon: Flag, label: deal.requestType, color: "#7c3aed", bg: "#f5f3ff" },
    { icon: Ticket, label: deal.existingTicketId && `Ticket ${deal.existingTicketId}`, color: "#b45309", bg: "#fffbeb" },
    { icon: CalendarClock, label: deal.preferredDateTime && formatDateTime(deal.preferredDateTime), color: "#be123c", bg: "#fff1f2" },
  ].filter((f) => !isEmpty(f.label));

  const priorityColor = PRIORITY_COLORS[String(deal.priority || "medium").toLowerCase()];

  const actions = [
    deal.mobile && { icon: Phone, label: "Call", href: `tel:+${String(deal.mobile).replace(/\D/g, "")}` },
    deal.mobile && { icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/${String(deal.mobile).replace(/\D/g, "")}`, external: true },
  ].filter(Boolean);

  return (
    // typography: theme font for plain Box text (values), not the browser default serif
    <Box sx={{ typography: "body2" }}>
      {/* ===== Summary ===== */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2,
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          borderLeft: `4px solid ${priorityColor?.main || "#F59E0B"}`,
          background: "linear-gradient(135deg, #f8faff 0%, #ffffff 60%)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Avatar sx={{ width: 52, height: 52, bgcolor: "#eef2ff", color: "#4f46e5", fontWeight: 700, fontSize: 18 }}>
            {initials(deal)}
          </Avatar>

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a", lineHeight: 1.3 }}>
                {deal.dealName || deal.name || "Customer"}
              </Typography>
              <Chip
                label={`${capitalize(deal.priority || "medium")} priority`}
                size="small"
                sx={{ bgcolor: priorityColor?.light, color: priorityColor?.text, fontWeight: 700, fontSize: 11, height: 20 }}
              />
            </Box>
            {location && (
              <Typography sx={{ fontSize: 13.5, color: "#64748b", mt: 0.25, display: "flex", alignItems: "center", gap: 0.5 }}>
                <MapPin size={13} /> {location}
              </Typography>
            )}
            {facts.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1.25 }}>
                {facts.map(({ icon: Icon, label, color, bg }) => (
                  <Chip
                    key={label}
                    size="small"
                    icon={<Icon size={13} color={color} />}
                    label={label}
                    sx={{ bgcolor: bg, color, fontWeight: 600, fontSize: 12, borderRadius: 1.5, "& .MuiChip-icon": { ml: 0.75 } }}
                  />
                ))}
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {!editing &&
              actions.map(({ icon: Icon, label, href, external }) => (
                <Tooltip key={label} title={label}>
                  <IconButton
                    component="a"
                    href={href}
                    target={external ? "_blank" : undefined}
                    rel={external ? "noopener noreferrer" : undefined}
                    sx={{ width: 36, height: 36, border: "1px solid #e2e8f0", bgcolor: "#fff", color: "#475569", "&:hover": { color: "#4f46e5", borderColor: "#c7d2fe", bgcolor: "#f5f7ff" } }}
                  >
                    <Icon size={16} />
                  </IconButton>
                </Tooltip>
              ))}
            {!isViewer && !editing && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Pencil size={14} />}
                onClick={startEdit}
                sx={{ ml: 0.5, height: 36, textTransform: "none", fontWeight: 600, borderRadius: 2, boxShadow: "none", bgcolor: "#4f46e5", "&:hover": { bgcolor: "#4338ca", boxShadow: "none" } }}
              >
                Edit request
              </Button>
            )}
            {editing && (
              <>
                <Button size="small" onClick={() => setEditing(false)} disabled={saving} sx={{ height: 36, textTransform: "none", color: "#64748b" }}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ height: 36, minWidth: 120, textTransform: "none", fontWeight: 600, borderRadius: 2, boxShadow: "none", bgcolor: "#4f46e5", "&:hover": { bgcolor: "#4338ca", boxShadow: "none" } }}
                >
                  {saving ? <CircularProgress size={16} color="inherit" /> : "Save changes"}
                </Button>
              </>
            )}
          </Box>
        </Box>

        {!editing && (
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 1.75 }}>
            {filledCount} of {visibleFields.length} details on file
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* ===== Sections ===== */}
      <Grid container spacing={2} alignItems="flex-start">
        {SECTIONS.map(({ title, icon: Icon, color, fields: allFields }) => {
          const fields = allFields.filter(isVisible);
          const filled = fields.filter((f) => !isEmpty(valueOf(f.key)));
          const notes = filled.filter((f) => f.multiline);
          const rows = editing ? fields : filled.filter((f) => !f.multiline);

          return (
            <Grid item xs={12} md={title === "Service details" ? 12 : 6} key={title}>
              <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.25, py: 1.5, borderBottom: "1px solid #f1f5f9", bgcolor: "#fcfcfd" }}>
                  <Box sx={{ p: 0.6, borderRadius: 1.5, bgcolor: `${color}14`, color, display: "flex" }}>
                    <Icon size={15} />
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: "#0f172a" }}>{title}</Typography>
                  {!editing && (
                    <Typography sx={{ ml: "auto", fontSize: 11.5, color: "#94a3b8" }}>
                      {filled.length}/{fields.length}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ px: 2.25, py: editing ? 2 : 0.5 }}>
                  {editing ? (
                    <Grid container spacing={1.5}>
                      {fields.map((field) => (
                        <Grid item xs={12} sm={field.full ? 12 : 6} key={field.key}>
                          {field.readOnly ? (
                            <Box sx={{ px: 1.5, py: 1, borderRadius: 1.5, bgcolor: "#f8fafc", border: "1px dashed #e2e8f0" }}>
                              <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{field.label}</Typography>
                              <Typography sx={{ fontSize: 14, color: "#334155" }}>{valueOf(field.key) || "—"}</Typography>
                              {field.hint && <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>{field.hint}</Typography>}
                            </Box>
                          ) : (
                            <TextField
                              fullWidth
                              size="small"
                              label={`${field.label}${isRequired(field) ? " *" : ""}`}
                              select={field.type === "select"}
                              type={field.type === "datetime" ? "datetime-local" : field.type === "number" ? "number" : "text"}
                              InputLabelProps={field.type === "datetime" ? { shrink: true } : undefined}
                              inputProps={field.type === "number" ? { min: 0, step: "0.01" } : undefined}
                              multiline={field.multiline}
                              minRows={field.multiline ? 2 : undefined}
                              value={form[field.key] ?? ""}
                              onChange={(e) => handleChange(field.key, e.target.value)}
                            >
                              {field.type === "select" &&
                                field.options.map((o) => (
                                  <MenuItem key={o} value={o}>
                                    {field.key === "priority" ? capitalize(o) : o}
                                  </MenuItem>
                                ))}
                            </TextField>
                          )}
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <>
                      {rows.map((field, i) => {
                        const value = valueOf(field.key);
                        return (
                          <Box
                            key={field.key}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              py: 1.1,
                              borderTop: i ? "1px solid #f1f5f9" : "none",
                              "&:hover .copy-btn": { opacity: 1 },
                            }}
                          >
                            <Typography sx={{ width: 140, flexShrink: 0, fontSize: 13, color: "#64748b" }}>{field.label}</Typography>
                            <Box sx={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, color: "#0f172a" }}>
                              {renderValue(field, value)}
                            </Box>
                            {field.copy && <CopyButton value={value} />}
                          </Box>
                        );
                      })}

                      {notes.map((field) => (
                        <Box key={field.key} sx={{ display: "flex", gap: 1.25, my: 1.25, p: 1.5, borderRadius: 2, bgcolor: "#f8fafc", borderLeft: `3px solid ${color}` }}>
                          <Quote size={15} color={color} style={{ flexShrink: 0, marginTop: 2 }} />
                          <Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.25 }}>
                              {field.label}
                            </Typography>
                            <Typography sx={{ fontSize: 13.5, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.55 }}>
                              {deal[field.key]}
                            </Typography>
                          </Box>
                        </Box>
                      ))}

                      {filled.length === 0 && (
                        <Typography sx={{ py: 2, fontSize: 13, color: "#94a3b8", fontStyle: "italic" }}>No details added yet.</Typography>
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default ServiceRequestTab;
